import { mapSchoolProfile } from './schoolProfileMapper';
import { normalizePublicPath } from '../utils/reservedPaths';

// TODO: replace with the production API base URL before launch.
export const SCHOOL_API_BASE_URL = 'https://classroom-store-test.azurewebsites.net';

const REQUEST_TIMEOUT_MS = 15000;

/** The path exists as a route but no school profile is published for it. */
export class SchoolProfileNotFoundError extends Error {
  constructor(publicPath) {
    super(`No school profile found for "${publicPath}"`);
    this.name = 'SchoolProfileNotFoundError';
    this.code = 'NOT_FOUND';
    this.publicPath = publicPath;
  }
}

/** Network failure, timeout, server error or unreadable payload. */
export class SchoolProfileRequestError extends Error {
  constructor(message, { status = null, cause = null } = {}) {
    super(message);
    this.name = 'SchoolProfileRequestError';
    this.code = 'REQUEST_FAILED';
    this.status = status;
    this.cause = cause;
  }
}

const buildProfileUrl = (publicPath) =>
  `${SCHOOL_API_BASE_URL}/school/public-profile/${encodeURIComponent(publicPath)}`;

const readJson = async (response) => {
  try {
    return await response.json();
  } catch (error) {
    return null;
  }
};

/**
 * Fetch and normalize a school's public profile.
 *
 * Resolves with the profile view model produced by `mapSchoolProfile`.
 * Rejects with `SchoolProfileNotFoundError` for 404 / empty results and with
 * `SchoolProfileRequestError` for everything else. Raw API errors are never
 * surfaced to the UI.
 *
 * @param {string} rawPublicPath school public path, e.g. "queens-school"
 * @param {{ signal?: AbortSignal }} [options]
 */
export const fetchSchoolPublicProfile = async (rawPublicPath, { signal } = {}) => {
  const publicPath = normalizePublicPath(rawPublicPath);

  if (!publicPath) {
    throw new SchoolProfileNotFoundError(rawPublicPath);
  }

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);

  const forwardAbort = () => controller.abort();
  if (signal) {
    if (signal.aborted) forwardAbort();
    signal.addEventListener('abort', forwardAbort);
  }

  let response;
  try {
    response = await fetch(buildProfileUrl(publicPath), {
      method: 'GET',
      headers: { Accept: 'application/json' },
      signal: controller.signal,
    });
  } catch (error) {
    // A caller-initiated abort is not a failure the UI should render.
    if (signal && signal.aborted) throw error;

    throw new SchoolProfileRequestError('Unable to reach the school profile service', {
      cause: error,
    });
  } finally {
    clearTimeout(timeoutId);
    if (signal) signal.removeEventListener('abort', forwardAbort);
  }

  if (response.status === 404) {
    throw new SchoolProfileNotFoundError(publicPath);
  }

  if (!response.ok) {
    throw new SchoolProfileRequestError('The school profile service returned an error', {
      status: response.status,
    });
  }

  const payload = await readJson(response);

  if (!payload || typeof payload !== 'object') {
    throw new SchoolProfileRequestError('The school profile response could not be read');
  }

  if (payload.status && payload.status !== 'success') {
    throw new SchoolProfileNotFoundError(publicPath);
  }

  const result = payload.result || payload.data;
  if (!result || typeof result !== 'object') {
    throw new SchoolProfileNotFoundError(publicPath);
  }

  return mapSchoolProfile(result, publicPath);
};
